import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Eye, CheckCircle, Clock } from 'lucide-react';
import EmptyState from '../../common/EmptyState';
import Pagination from '../../common/Pagination';

const ProcesVerbalList = ({
  procesVerbaux,
  loading,
  onEdit,
  onDelete,
  onView,
  onValidate,
  pagination,
  onPageChange,
  onPageSizeChange
}) => {
  const navigate = useNavigate();
  const mentions = [
    { value: 'PASSABLE', label: 'Passable', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'ASSEZ_BIEN', label: 'Assez bien', color: 'bg-blue-100 text-blue-800' },
    { value: 'BIEN', label: 'Bien', color: 'bg-green-100 text-green-800' },
    { value: 'TRES_BIEN', label: 'Très bien', color: 'bg-emerald-100 text-emerald-800' },
    { value: 'EXCELLENT', label: 'Excellent', color: 'bg-purple-100 text-purple-800' }
  ];

  const decisions = [
    { value: 'ADMIS', label: 'Admis', color: 'bg-green-100 text-green-800' },
    { value: 'ADMIS_RESERVE', label: 'Admis avec réserve', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'AJOURNE', label: 'Ajourné', color: 'bg-red-100 text-red-800' },
    { value: 'REPORTE', label: 'Reporté', color: 'bg-orange-100 text-orange-800' }
  ];

  const getMentionInfo = (mention) => {
    return mentions.find(m => m.value === mention) || mentions[0];
  };

  const getDecisionInfo = (decision) => {
    return decisions.find(d => d.value === decision) || decisions[0];
  };

  const getSoutenanceLabel = (soutenance) => {
    if (!soutenance) return 'Non définie';
    
    let etudiantsNoms = '';
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      const etudiant = soutenance.etudiants_soutenance[0].etudiant;
      etudiantsNoms = `${etudiant.nom} ${etudiant.prenom}`;
    } else if (soutenance.etudiant_principal_info) {
      etudiantsNoms = `${soutenance.etudiant_principal_info.nom} ${soutenance.etudiant_principal_info.prenom}`;
    }
    
    return etudiantsNoms || soutenance.titre_travail?.substring(0, 40) || 'Soutenance';
  };

  if (loading && procesVerbaux.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (procesVerbaux.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="Aucun procès-verbal"
        description="Aucun procès-verbal trouvé."
        actionLabel="Créer un PV"
        onAction={() => onEdit()}
      />
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Soutenance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Note
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mention
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Décision
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {procesVerbaux.map((pv) => {
              const mentionInfo = getMentionInfo(pv.mention);
              const decisionInfo = getDecisionInfo(pv.decision);
              const signaturesCompletes = pv.date_signature_president && pv.date_signature_rapporteur && pv.date_signature_examinateur;
              
              return (
                <tr key={pv.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {getSoutenanceLabel(pv.soutenance)}
                        </div>
                        {pv.soutenance?.titre_travail && (
                          <div className="text-sm text-gray-500 truncate">
                            {pv.soutenance.titre_travail}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-lg font-bold text-blue-600">
                      {pv.note_finale}/20
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${mentionInfo.color}`}>
                      {mentionInfo.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${decisionInfo.color}`}>
                      {decisionInfo.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {pv.valide ? (
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <Clock className="h-5 w-5 text-yellow-500 mr-2" />
                      )}
                      <span className={`text-sm ${pv.valide ? 'text-green-800' : 'text-yellow-800'}`}>
                        {pv.valide ? 'Validé' : 'En attente'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/proces-verbal/${pv.id}`)}
                        className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-50 transition-colors"
                        title="Voir le détail"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination && pagination.total_pages > 1 && (
        <Pagination
          pagination={pagination}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};

export default ProcesVerbalList; 